import { Link } from 'react-router-dom';
import type { GrahaBody, VargaChart } from '@astrally/shared';
import { RASHI_NAMES } from '@astrally/shared';
import { useI18n } from '../../i18n';
import { KUNDLI_GUIDE_ANCHOR } from './ChartStyleGuide';
import {
  ExplainChip,
  HouseHitArea,
  KundliPopover,
  useKundliTouch,
  type HouseTapInfo,
} from './KundliTouch';

/**
 * East Indian (Bengali / Odia / Assamese) chart — the third major Indian
 * format, used across West Bengal, Odisha, Assam and Tripura.
 *
 * Like the South Indian chart the SIGNS ARE FIXED and the lagna is marked
 * rather than rotated, but the geometry differs: a square whose corner cells
 * are split by short diagonals, with the four cardinal cells left whole. The
 * traditional arrangement runs ANTICLOCKWISE, and the chart is conventionally
 * labelled in the centre.
 *
 * Fixed layout (anticlockwise from the top-centre cell):
 *                Aries
 *      Pisces  ╱       ╲  Taurus
 *   Aquarius                Gemini
 *      Capricorn  ╲   ╱  Cancer
 *                Leo …
 *
 * Implemented as a 4×4 grid so the twelve perimeter cells map cleanly, with
 * the corner cells carrying the diagonal split that distinguishes the style.
 */

const PLANET_ABBR: Record<string, string> = {
  sun: 'Su',
  moon: 'Mo',
  mars: 'Ma',
  mercury: 'Me',
  jupiter: 'Ju',
  venus: 'Ve',
  saturn: 'Sa',
  rahu: 'Ra',
  ketu: 'Ke',
  uranus: 'Ur',
  neptune: 'Ne',
  pluto: 'Pl',
  lagna: 'La',
};

/**
 * sign index (0 = Aries) → [column, row] in the 4×4 grid, running
 * ANTICLOCKWISE from Aries at top-centre-left.
 */
const SIGN_CELL: Record<number, [number, number]> = {
  0: [1, 0], // Aries      top row
  11: [0, 0], // Pisces     top-left corner
  10: [0, 1], // Aquarius   left column
  9: [0, 2], // Capricorn  left column
  8: [0, 3], // Sagittarius bottom-left corner
  7: [1, 3], // Scorpio    bottom row
  6: [2, 3], // Libra      bottom row
  5: [3, 3], // Virgo      bottom-right corner
  4: [3, 2], // Leo        right column
  3: [3, 1], // Cancer     right column
  2: [3, 0], // Gemini     top-right corner
  1: [2, 0], // Taurus     top row
};

/** Corner cells carry the diagonal that characterises the East Indian style. */
const CORNERS = new Set(['0,0', '3,0', '0,3', '3,3']);

const SIZE = 300;
const CELL = SIZE / 4;

export function EastIndianChart({ varga, title }: { varga: VargaChart; title?: string }) {
  const { t } = useI18n();
  const touch = useKundliTouch();
  const lagna = varga.placements.find((p) => p.body === 'lagna');

  const bySign = new Map<number, GrahaBody[]>();
  for (const p of varga.placements) {
    if (p.body === 'lagna') continue;
    if (['uranus', 'neptune', 'pluto'].includes(p.body)) continue;
    const list = bySign.get(p.sign) ?? [];
    list.push(p.body as GrahaBody);
    bySign.set(p.sign, list);
  }

  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="spread" style={{ marginBottom: 8 }}>
        {title ? <div className="h3">{title}</div> : <span aria-hidden />}
        <ExplainChip on={touch.explain} onToggle={touch.toggleExplain} />
      </div>
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ width: '100%', height: 'auto' }}>
        <rect
          x="1"
          y="1"
          width={SIZE - 2}
          height={SIZE - 2}
          fill="none"
          stroke="var(--border-strong)"
          strokeWidth="1.5"
          rx="6"
        />

        {Object.entries(SIGN_CELL).map(([signStr, [col, row]]) => {
          const sign = Number(signStr);
          const x = col * CELL;
          const y = row * CELL;
          const isLagna = lagna?.sign === sign;
          const planets = bySign.get(sign) ?? [];
          const isCorner = CORNERS.has(`${col},${row}`);

          // Corner diagonals run toward the chart centre.
          const diag = isCorner
            ? col === 0 && row === 0
              ? `M${x} ${y + CELL} L${x + CELL} ${y}`
              : col === 3 && row === 0
                ? `M${x} ${y} L${x + CELL} ${y + CELL}`
                : col === 0 && row === 3
                  ? `M${x} ${y} L${x + CELL} ${y + CELL}`
                  : `M${x} ${y + CELL} L${x + CELL} ${y}`
            : null;

          return (
            <g key={sign}>
              <rect
                x={x}
                y={y}
                width={CELL}
                height={CELL}
                fill={isLagna ? 'var(--gold-soft)' : 'transparent'}
                stroke="var(--border-strong)"
                strokeWidth="1"
              />
              {diag && <path d={diag} stroke="var(--border)" strokeWidth="1" fill="none" />}
              <text
                x={x + CELL - 5}
                y={y + 12}
                textAnchor="end"
                fontSize="9"
                fill="var(--text-faint)"
              >
                {RASHI_NAMES[sign].slice(0, 4)}
              </text>
              {isLagna && (
                <text x={x + 6} y={y + 12} fontSize="9" fontWeight="700" fill="var(--gold-ink)">
                  La
                </text>
              )}
              {planets.map((body, i) => (
                <text
                  key={body}
                  x={x + 8}
                  y={y + 32 + i * 14}
                  fontSize="12"
                  fontWeight="600"
                  fill={isLagna ? 'var(--gold-ink)' : 'var(--text)'}
                >
                  {PLANET_ABBR[body]}
                </text>
              ))}
              {/* The bhava this fixed rashi cell is, counted from the Lagna —
                  the visible house denotation the Western wheel has always
                  had. Bottom corner: the rashi label owns the top-right, the
                  "La" mark the top-left, the graha column the left edge.
                  No Lagna, no count — window-mode charts skip it honestly. */}
              {lagna && (
                <text
                  x={x + CELL - 5}
                  y={y + CELL - 6}
                  textAnchor="end"
                  fontSize="9"
                  fill="var(--text-faint)"
                >
                  {((sign - lagna.sign + 12) % 12) + 1}
                </text>
              )}
            </g>
          );
        })}

        <text
          x={SIZE / 2}
          y={SIZE / 2 - 2}
          textAnchor="middle"
          fontSize="16"
          fontWeight="700"
          fill="var(--text-faint)"
        >
          {varga.varga}
        </text>
        <text
          x={SIZE / 2}
          y={SIZE / 2 + 16}
          textAnchor="middle"
          fontSize="9"
          fill="var(--text-faint)"
        >
          East Indian
        </text>

        {/* Hit areas last, so they sit on top of every painted layer. Signs
            are fixed here, so bhava numbers exist only when the lagna does —
            without one the tap honestly offers the rashi and its grahas. */}
        {Object.entries(SIGN_CELL).map(([signStr, [col, row]]) => {
          const sign = Number(signStr);
          const info: HouseTapInfo = {
            bhava: lagna ? ((sign - lagna.sign + 12) % 12) + 1 : null,
            sign,
            grahas: bySign.get(sign) ?? [],
          };
          return (
            <HouseHitArea
              key={`hit-${sign}`}
              shape={{ kind: 'rect', x: col * CELL, y: row * CELL, width: CELL, height: CELL }}
              info={info}
              explain={touch.explain}
              active={touch.active?.info.sign === sign}
              onTap={touch.tap}
            />
          );
        })}
      </svg>
      <div className="tiny faint center" style={{ marginTop: 6 }}>
        {varga.varga} — {varga.description}
      </div>
      <div className="tiny faint center" style={{ marginTop: 4 }}>
        {lagna ? t('kundli.legend.fixed') : t('kundli.legend.noLagna')}
      </div>
      <div className="center" style={{ marginTop: 6 }}>
        <Link to={`/charts#${KUNDLI_GUIDE_ANCHOR}`} className="tiny accent">
          {t('kundli.howToRead')} →
        </Link>
      </div>
      {touch.active && (
        <KundliPopover info={touch.active.info} anchor={touch.active.anchor} onClose={touch.close} />
      )}
    </div>
  );
}
